// ============================================================================
// CHANGO V5.0 — D3 App Settings (tabbed)
// ============================================================================
// 5-tab skeleton per plan:
//   - 账号      (every role)
//   - 成员      (boss)
//   - 工作区    (boss)
//   - 账单      (boss, finance)
//   - 协议      (every role — read-only)
//
// Each tab renders a minimal panel; mutations go through the existing
// PATCH /api/me (account), PATCH /api/workspace/:id (workspace/members),
// and GET /api/legal/* endpoints. Later passes will deepen each tab.
// ============================================================================

import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { useAuth } from '../../hooks/useAuth.js';
import { api, HttpError } from '../../api/client.js';
import RoleGate from '../../components/RoleGate.jsx';
import { brandForSku } from '../../lib/skuBrandVersion.js';

const TABS = [
  { key: 'account',   label: '账号',   allow: null },
  { key: 'members',   label: '成员',   allow: ['boss'] },
  { key: 'workspace', label: '工作区', allow: ['boss'] },
  { key: 'billing',   label: '账单',   allow: ['boss', 'finance'] },
  { key: 'legal',     label: '协议',   allow: null }
];

function AccountPanel({ user }) {
  return (
    <section className="space-y-3">
      <h2 className="text-base font-semibold text-slate-800">账号信息</h2>
      <div className="card p-5 space-y-2 text-sm">
        <div className="flex justify-between"><span className="text-slate-500">邮箱</span><span>{user?.email}</span></div>
        <div className="flex justify-between"><span className="text-slate-500">角色</span><span>{user?.role || '—'}</span></div>
        <div className="flex justify-between"><span className="text-slate-500">2FA</span><span>{user?.two_fa_enabled ? '已启用' : '未启用'}</span></div>
      </div>
      <div className="text-xs text-slate-500">
        修改密码 / 启用 2FA / 更新邮箱 — D3 后续补齐。
      </div>
    </section>
  );
}

function MembersPanel() {
  return (
    <section className="space-y-3">
      <h2 className="text-base font-semibold text-slate-800">成员</h2>
      <div className="card p-5 text-sm">
        <p className="text-slate-600">
          完整成员管理在{' '}
          <Link to="/workspace/settings" className="text-chango-700 hover:underline">/workspace/settings</Link>
          {' '}(仅 boss 可访问)。此面板为快捷入口。
        </p>
      </div>
    </section>
  );
}

function WorkspacePanel({ workspace }) {
  return (
    <section className="space-y-3">
      <h2 className="text-base font-semibold text-slate-800">工作区</h2>
      <div className="card p-5 space-y-2 text-sm">
        <div className="flex justify-between"><span className="text-slate-500">名称</span><span>{workspace?.name || '—'}</span></div>
        <div className="flex justify-between"><span className="text-slate-500">公司全称</span><span>{workspace?.company_name || '—'}</span></div>
        <div className="flex justify-between"><span className="text-slate-500">套餐 SKU</span><span>{workspace?.sku_code || '—'}</span></div>
        <div className="flex justify-between"><span className="text-slate-500">注册状态</span><span>{workspace?.registration_status || '—'}</span></div>
        <div className="flex justify-between"><span className="text-slate-500">UI 模式</span><span>{workspace?.ui_mode || '—'}</span></div>
      </div>
      <Link to="/workspace/settings" className="btn-primary inline-block w-fit">
        编辑工作区设置
      </Link>
    </section>
  );
}

// ---- D11 Billing panel (live /api/billing/skus + /orders) ----------------

const BILLING_CYCLE_LABEL = {
  monthly:   '月',
  yearly:    '年',
  one_time:  '一次性',
  perpetual: '永久'
};

const ORDER_STATUS_LABEL = {
  awaiting_payment: '待支付',
  paid:             '已支付',
  cancelled:        '已取消',
  refund_pending:   '退款中',
  refunded:         '已退款',
  failed:           '失败'
};

function formatPrice(amount_rmb, cycle) {
  if (amount_rmb == null || amount_rmb === '') return '敬请联系';
  const n = Number(amount_rmb);
  const pretty = Number.isFinite(n)
    ? n.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : amount_rmb;
  const suffix = cycle && BILLING_CYCLE_LABEL[cycle] ? ` / ${BILLING_CYCLE_LABEL[cycle]}` : '';
  return `¥${pretty}${suffix}`;
}

function BillingPanel({ workspace }) {
  const [skus, setSkus] = useState(null);
  const [orders, setOrders] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [s, o] = await Promise.all([
          api.get('/api/billing/skus').catch(() => ({ skus: [] })),
          api.get('/api/billing/orders').catch((e) => {
            if (e instanceof HttpError && e.status === 403) return { orders: [] };
            throw e;
          })
        ]);
        if (cancelled) return;
        setSkus(Array.isArray(s?.skus) ? s.skus : []);
        setOrders(Array.isArray(o?.orders) ? o.orders : (Array.isArray(o) ? o : []));
      } catch (e) {
        if (cancelled) return;
        setErr(e instanceof HttpError ? `${e.code}: ${e.message}` : (e.message || '加载失败'));
        setSkus([]); setOrders([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <section className="space-y-4">
      <h2 className="text-base font-semibold text-slate-800">账单</h2>

      <div className="card p-5 space-y-2 text-sm">
        <div className="flex justify-between"><span className="text-slate-500">套餐 SKU</span><span>{workspace?.sku_code || '—'}</span></div>
        <div className="flex justify-between"><span className="text-slate-500">试用到期</span><span>{workspace?.trial_expires_at || '—'}</span></div>
        <div className="flex justify-between"><span className="text-slate-500">订阅到期</span><span>{workspace?.expires_at || '—'}</span></div>
      </div>

      {err && <div className="alert-error">{err}</div>}

      <div className="card p-5">
        <h3 className="mb-3 text-sm font-semibold text-slate-700">可选套餐 (SKU)</h3>
        {skus == null && <p className="text-sm text-slate-400">加载中…</p>}
        {Array.isArray(skus) && skus.length === 0 && (
          <p className="text-sm text-slate-500">暂无可选 SKU，请联系客服。</p>
        )}
        {Array.isArray(skus) && skus.length > 0 && (
          <ul className="divide-y divide-slate-100 text-sm">
            {skus.map((s) => {
              const info = brandForSku(s.sku_code);
              return (
              <li key={s.sku_code} className="flex items-center justify-between py-2">
                <div>
                  <div className="font-medium text-slate-800">
                    <span className={`mr-2 inline-block rounded px-1.5 py-0.5 text-[10px] font-medium ${
                      info.series === 'A' ? 'bg-blue-50 text-blue-700'
                      : info.series === 'B' ? 'bg-emerald-50 text-emerald-700'
                      : 'bg-amber-50 text-amber-700'
                    }`}>
                      {info.series === 'A' ? 'A 系' : info.series === 'B' ? 'B 系' : '加购'}
                    </span>
                    {info.shortName}
                  </div>
                  <div className="text-xs text-slate-500">
                    {info.fullName} · 代码 {s.sku_code}
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-medium tabular-nums text-slate-800">
                    {formatPrice(s.amount_rmb, s.billing_cycle)}
                  </div>
                  {!s.available && (
                    <div className="text-xs text-slate-400">敬请联系</div>
                  )}
                </div>
              </li>
              );
            })}
          </ul>
        )}
        <p className="mt-3 text-xs text-slate-400">
          在线支付通道接入中，当前下单请联系客服。
        </p>
      </div>

      <div className="card p-5">
        <h3 className="mb-3 text-sm font-semibold text-slate-700">订单记录</h3>
        {orders == null && <p className="text-sm text-slate-400">加载中…</p>}
        {Array.isArray(orders) && orders.length === 0 && (
          <p className="text-sm text-slate-500">暂无订单。</p>
        )}
        {Array.isArray(orders) && orders.length > 0 && (
          <ul className="divide-y divide-slate-100 text-sm">
            {orders.map((o) => (
              <li key={o.id || o.order_code} className="flex items-center justify-between py-2">
                <div>
                  <div className="font-mono text-xs text-slate-600">{o.order_code || `#${o.id}`}</div>
                  <div className="text-xs text-slate-500">
                    {brandForSku(o.sku_code).shortName} × {o.qty ?? 1}
                    {o.created_at && ` · ${new Date(o.created_at).toLocaleDateString('zh-CN')}`}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="tabular-nums text-slate-800">
                    {formatPrice(o.amount_rmb, null)}
                  </span>
                  <span className="rounded bg-slate-100 px-2 py-0.5 text-xs text-slate-700">
                    {ORDER_STATUS_LABEL[o.status] || o.status || '—'}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

function LegalPanel() {
  return (
    <section className="space-y-3">
      <h2 className="text-base font-semibold text-slate-800">协议</h2>
      <div className="card p-5 space-y-2 text-sm">
        <Link to="/legal/terms"   className="text-chango-700 hover:underline">服务条款 (Terms of Service)</Link>
        <br />
        <Link to="/legal/privacy" className="text-chango-700 hover:underline">隐私政策 (Privacy Policy)</Link>
      </div>
      <p className="text-xs text-slate-500">
        您同意的协议版本及时间戳由 <code>user_agreement_consents</code> 表留存。
      </p>
    </section>
  );
}

export default function AppSettings() {
  const { user, workspace } = useAuth();
  const [params, setParams] = useSearchParams();
  const activeKey = params.get('tab') || 'account';

  const visibleTabs = useMemo(
    () => TABS.filter((t) => !t.allow || (user?.role && t.allow.includes(user.role))),
    [user?.role]
  );
  const active = visibleTabs.find((t) => t.key === activeKey) || visibleTabs[0];

  const selectTab = (key) => {
    const next = new URLSearchParams(params);
    next.set('tab', key);
    setParams(next, { replace: true });
  };

  return (
    <div className="px-6 py-6 max-w-5xl mx-auto">
      <h1 className="text-xl font-semibold text-slate-800">设置</h1>
      <p className="text-sm text-slate-500 mt-1">账号 / 工作区 / 协议</p>

      <nav className="mt-6 border-b border-slate-200 flex gap-5" role="tablist">
        {visibleTabs.map((t) => {
          const isActive = t.key === active?.key;
          return (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={isActive}
              onClick={() => selectTab(t.key)}
              className={[
                '-mb-px border-b-2 pb-3 text-sm',
                isActive
                  ? 'border-chango-600 text-chango-700 font-semibold'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              ].join(' ')}
            >
              {t.label}
            </button>
          );
        })}
      </nav>

      <div className="mt-6">
        {active?.key === 'account'   && <AccountPanel user={user} />}
        {active?.key === 'members'   && (
          <RoleGate allow={['boss']} fallback={<p className="text-sm text-slate-500">仅 boss 可查看。</p>}>
            <MembersPanel />
          </RoleGate>
        )}
        {active?.key === 'workspace' && (
          <RoleGate allow={['boss']} fallback={<p className="text-sm text-slate-500">仅 boss 可查看。</p>}>
            <WorkspacePanel workspace={workspace} />
          </RoleGate>
        )}
        {active?.key === 'billing'   && (
          <RoleGate
            allow={['boss', 'finance']}
            fallback={<p className="text-sm text-slate-500">仅 boss / finance 可查看。</p>}
          >
            <BillingPanel workspace={workspace} />
          </RoleGate>
        )}
        {active?.key === 'legal'     && <LegalPanel />}
      </div>
    </div>
  );
}
